/**
 * RankBadges — Public rank tier display for the trust center profile
 *
 * Fetches the public rank summary (no auth required) for a given tenant
 * and renders compact CR/SR/PR tier badges. Only shows non-unranked tracks
 * unless all are unranked (then renders nothing).
 *
 * Usage:
 *   <RankBadges tenantId={currentTenant.id} />
 */

import React, { useState, useEffect } from 'react'
import { Shield, Lock, Eye } from 'lucide-react'

interface PublicRankSummary {
  tenant_id: string
  public_summary: {
    compliance_tier: string
    security_tier: string
    privacy_tier: string
  }
}

interface RankBadgesProps {
  tenantId: string
  className?: string
}

const TIER_STYLE: Record<string, { label: string; color: string; bg: string; border: string; icon: string }> = {
  grandmaster: { label: 'Grandmaster', color: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200',  icon: '♛' },
  advanced:    { label: 'Advanced',    color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', icon: '◆' },
  proficient:  { label: 'Proficient',  color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200', icon: '▲' },
  established: { label: 'Established', color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200',   icon: '●' },
  provisional: { label: 'Provisional', color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: '○' },
}

const TRACK_META = {
  compliance: { label: 'Compliance Rank', Icon: Shield },
  security:   { label: 'Security Rank',   Icon: Lock   },
  privacy:    { label: 'Privacy Rank',    Icon: Eye    },
} as const

export default function RankBadges({ tenantId, className = '' }: RankBadgesProps) {
  const [data, setData] = useState<PublicRankSummary | null>(null)

  useEffect(() => {
    if (!tenantId) return
    fetch(`/api/skills/rank?tenant_id=${encodeURIComponent(tenantId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => null)
  }, [tenantId])

  if (!data?.public_summary) return null

  const { compliance_tier, security_tier, privacy_tier } = data.public_summary

  const tracks = [
    { key: 'compliance', tier: compliance_tier, ...TRACK_META.compliance },
    { key: 'security',   tier: security_tier,   ...TRACK_META.security   },
    { key: 'privacy',    tier: privacy_tier,    ...TRACK_META.privacy    },
  ].filter(t => t.tier && t.tier !== 'unranked')

  if (tracks.length === 0) return null

  return (
    <div className={`${className}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-gray-400 font-medium tracking-wide uppercase">Skills Rank</span>
        {tracks.map(({ key, tier, label, Icon }) => {
          const style = TIER_STYLE[tier]
          if (!style) return null
          return (
            <div
              key={key}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${style.bg} ${style.color} ${style.border}`}
              title={`${label}: ${style.label} tier — verified by TrustCenterPage Skills Registry`}
            >
              <Icon className="h-3 w-3" />
              <span>{label.split(' ')[0]}</span>
              <span className="opacity-60">·</span>
              <span>{style.icon} {style.label}</span>
            </div>
          )
        })}
        <a
          href="https://trustcenterpage.com/skills-spec"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2"
        >
          What is this?
        </a>
      </div>
    </div>
  )
}
